import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Shield } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { PaymentChip } from '@/components/ui/Chip';
import { ResponsiveTable } from '@/components/ui/Table';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { usePatientInvoices } from '@/hooks/usePatientInvoices';
import { formatMoney, useInvoiceReminderLevels } from '@/hooks/useInvoices';
import { InvoiceStatusBadges } from '@/pages/invoices/Invoices';
import { uiDateLocale } from '@/lib/i18n';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';

interface InvoiceTabProps {
  patientId: string;
  clinicId: string;
}

function InvoiceReminderLevelBadge({ level }: { level: number | undefined }) {
  const { t } = useTranslation();
  if (level == null) return null;
  return (
    <PaymentChip>
      {t('invoices.reminder_level', {
        number: level,
        defaultValue: 'Rykker {{number}}',
      })}
    </PaymentChip>
  );
}

export function InvoiceTab({ patientId, clinicId }: InvoiceTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: invoices, isLoading, error, refetch } = usePatientInvoices(patientId, {}, clinicId);
  const invoiceIds = invoices?.map((invoice) => invoice.id) ?? [];
  const { data: reminderLevels = {} } = useInvoiceReminderLevels(invoiceIds);

  const handleCreateInvoice = () => {
    const params = new URLSearchParams({
      patient_id: patientId,
      clinic_id: clinicId,
      return_to: `/patients/${patientId}`,
    });
    navigate(`/invoices/new?${params.toString()}`);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString(uiDateLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isOverdue = (invoice: NonNullable<typeof invoices>[number]): boolean => {
    if (
      invoice.kind === 'credit_note' ||
      invoice.status === 'paid' ||
      invoice.status === 'void' ||
      !invoice.due_at
    ) {
      return false;
    }
    return new Date(invoice.due_at) < new Date();
  };

  const summary = useMemo(() => {
    const list = invoices ?? [];
    const outstanding = list.filter(
      (invoice) =>
        invoice.kind !== 'credit_note' &&
        invoice.status !== 'paid' &&
        invoice.status !== 'void' &&
        invoice.status !== 'refunded',
    );
    const overdue = list.filter((invoice) => isOverdue(invoice));
    const year = new Date().getFullYear();
    const paidYear = list.filter(
      (invoice) =>
        invoice.status === 'paid' &&
        invoice.issued_at &&
        new Date(invoice.issued_at).getFullYear() === year,
    );
    const sum = (rows: typeof list) => rows.reduce((acc, invoice) => acc + invoice.total_cents, 0);
    return {
      outstandingCount: outstanding.length,
      outstanding: sum(outstanding),
      overdueCount: overdue.length,
      overdue: sum(overdue),
      paidCount: paidYear.length,
      paid: sum(paidYear),
      year,
    };
  }, [invoices]);

  const empty = !isLoading && !error && (!invoices || invoices.length === 0);

  return (
    <PatientTabFrame
      title={t('patients.tabs.invoices')}
      count={invoices?.length ?? 0}
      action={
        <Button type="button" onClick={handleCreateInvoice}>
          {t('invoices.new_invoice')}
        </Button>
      }
      loading={isLoading}
      error={error ? t('invoices.load_error') : undefined}
      onRetry={() => void refetch()}
      empty={
        empty
          ? {
              icon: <FileText className="h-7 w-7" />,
              title: t('invoices.no_invoices_title'),
              description: t('invoices.no_invoices_description'),
            }
          : undefined
      }
    >
      <>
        <div className="mt-s4 flex flex-wrap items-end gap-x-s6 gap-y-s3 border-b border-ds-line pb-s4">
          <Metric
            label={t('invoices.outstanding')}
            value={formatMoney(summary.outstanding)}
            hint={t('invoices.invoice_count', { count: summary.outstandingCount })}
          />
          <Metric
            label={t('invoices.overdue')}
            value={formatMoney(summary.overdue)}
            hint={t('invoices.invoice_count', { count: summary.overdueCount })}
            danger
          />
          <Metric
            label={t('invoices.paid_in_year', { year: summary.year })}
            value={formatMoney(summary.paid)}
            hint={t('invoices.invoice_count', { count: summary.paidCount })}
          />
        </div>
        <div className="mt-s4">
            <ResponsiveTable
              rows={invoices ?? []}
              getRowKey={(invoice) => invoice.id}
              columns={[
                {
                  id: 'number',
                  header: t('invoices.col_invoice_number', 'Invoice #'),
                  cell: (invoice) => (
                    <span className="inline-flex items-center gap-s2">
                      <button
                        type="button"
                        className={cn(TYPE.body, 'font-medium text-ds-text')}
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                      >
                        {invoice.invoice_number || t('invoices.status_draft', 'Draft')}
                      </button>
                      {(invoice as { sd_submitted_at?: string | null }).sd_submitted_at ? (
                        <Shield className="h-4 w-4 text-ds-green" />
                      ) : null}
                    </span>
                  ),
                },
                {
                  id: 'date',
                  header: t('invoices.col_date', 'Date'),
                  cell: (invoice) => formatDate(invoice.issued_at || invoice.created_at),
                },
                {
                  id: 'due',
                  header: t('invoices.col_due_date', 'Due Date'),
                  cell: (invoice) => {
                    if (invoice.status === 'paid') {
                      const paidOn = invoice.paid_at ?? invoice.last_payment_at;
                      return paidOn
                        ? t('invoices.paid_on', { date: formatDate(paidOn) })
                        : t('invoices.paid_at_label');
                    }
                    return (
                      <span className={cn(isOverdue(invoice) && 'font-semibold text-ds-red-ink')}>
                        {formatDate(invoice.due_at)}
                      </span>
                    );
                  },
                },
                {
                  id: 'amount',
                  header: t('invoices.col_amount', 'Amount'),
                  numeric: true,
                  cell: (invoice) =>
                    formatMoney(
                      invoice.kind === 'credit_note' ? -Math.abs(invoice.total_cents) : invoice.total_cents,
                      invoice.currency,
                    ),
                },
                {
                  id: 'status',
                  header: t('invoices.col_status', 'Status'),
                  cell: (invoice) => (
                    <span className="inline-flex flex-wrap items-center gap-s2">
                      {invoice.kind === 'credit_note' ? (
                        <span className="font-medium">{t('invoices.kind_credit_note')}</span>
                      ) : (
                      <InvoiceStatusBadges
                        status={invoice.status}
                        lastSentAt={invoice.last_sent_at}
                      />
                      )}
                      {isOverdue(invoice) && invoice.status === 'issued' ? (
                        <span className="text-ds-red-ink">{t('invoices.overdue', 'Overdue')}</span>
                      ) : null}
                      <InvoiceReminderLevelBadge level={reminderLevels[invoice.id]} />
                    </span>
                  ),
                },
              ]}
            />
          </div>
      </>
    </PatientTabFrame>
  );
}

function Metric({
  label,
  value,
  hint,
  danger,
}: {
  label: string;
  value: string;
  hint: string;
  danger?: boolean;
}) {
  return (
    <div className="min-w-[8rem]">
      <p className={cn(TYPE.label, 'text-ds-muted')}>{label}</p>
      <p className={cn(TYPE.heading, 'mt-s1 tabular-nums', danger ? 'text-ds-red-ink' : 'text-ds-text')}>
        {value}
      </p>
      <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{hint}</p>
    </div>
  );
}
